/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */

import axios from 'axios';
import logger from '../utils/logger';

// Helper to ensure API path is correct
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Fix API paths to avoid duplicate /api
const makeApiPath = (path: string) => {
  // If API_URL already includes /api, don't add it again
  if (API_URL.endsWith('/api')) {
    return `${API_URL}${path.startsWith('/') ? path : '/' + path}`;
  }
  
  // Otherwise, include /api in the path
  return `${API_URL}/api${path.startsWith('/') ? path : '/' + path}`;
};

// User type
export interface User {
  _id: string;
  id: string;
  username: string;
  email: string;
}

// Cache for user lookups (username -> userId)
const userIdCache: Record<string, string> = {};

// Function to find a user ID by username
export const getUserByUsername = async (username: string): Promise<User | null> => {
  try {
    // Use token if available for authentication
    const token = localStorage.getItem('token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Use a search endpoint if available
    // Note: This implementation assumes the server has a search endpoint
    // If not, this is a placeholder for a future implementation
    try {
      // Try searching for users by username
      const response = await axios.get(makeApiPath(`/users/search?username=${encodeURIComponent(username)}`), { headers });
      
      if (response.data && response.data.length > 0) {
        const user = response.data[0];
        // Ensure id property is set
        if (user._id && !user.id) {
          user.id = user._id;
        }
        // Cache the result
        userIdCache[username] = user.id || user._id;
        return user;
      }
    } catch (searchError) {
      logger.warn('User search endpoint not available:', searchError);
      // Fall through to try any other methods
    }
    
    // If we reach here, search endpoint didn't work - try to get all users if possible
    try {
      // This is a fallback that won't scale well but can work for development
      const response = await axios.get(makeApiPath('/users'), { headers });
      
      if (response.data && Array.isArray(response.data)) {
        const user = response.data.find((u: any) => u.username === username);
        if (user) {
          // Ensure id property is set
          if (user._id && !user.id) {
            user.id = user._id;
          }
          // Cache the result
          userIdCache[username] = user.id || user._id;
          return user;
        }
      }
    } catch (getAllError) {
      logger.warn('Unable to get all users:', getAllError);
    }
    
    return null;
  } catch (error) {
    logger.error(`Error looking up user by username "${username}":`, error);
    return null;
  }
};

// Extended user profile with metrics
export interface UserProfile extends User {
  publicDebatesCount: number;
  debatesWonCount: number;
  totalDebatesCount: number;
  winRate: number;
  excaliburIndex: number;
  qualityMetrics?: {
    explanationStrength: number;
    argumentClarity: number;
    rebuttalEffectiveness: number;
    evidenceQuality: number;
    steelmanningRating: number;
  };
  createdAt?: string;
  bio?: string;
  profileImageUrl?: string;
  isOwner?: boolean;
  debates?: {
    id: string;
    topic: string;
    createdAt: string;
    status: string;
    isWinner: boolean;
    isPublic?: boolean;
  }[];
}

// Get current user
export const getCurrentUser = async (): Promise<User> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No auth token found');
    }
    
    const response = await axios.get(makeApiPath('/auth/user'), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // Ensure id property is set
    if (response.data._id && !response.data.id) {
      response.data.id = response.data._id;
    }
    
    return response.data;
  } catch (error) {
    logger.error('Error fetching current user:', error);
    throw error;
  }
};

// Get user by ID
export const getUserById = async (userId: string): Promise<User> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No auth token found');
    }
    
    const response = await axios.get(makeApiPath(`/users/${userId}`), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // Ensure id property is set
    if (response.data._id && !response.data.id) {
      response.data.id = response.data._id;
    }
    
    return response.data;
  } catch (error) {
    logger.error(`Error fetching user with ID ${userId}:`, error);
    throw error;
  }
};

// Get user profile with metrics
export const getUserProfile = async (userId: string): Promise<UserProfile> => {
  try {
    // Try with token first if available
    const token = localStorage.getItem('token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Check if userId is a MongoDB ID (24 hex chars) or a username
    const isMongoId = /^[0-9a-f]{24}$/i.test(userId);
    
    // If it's not a MongoDB ID, it might be a username - try to look it up first
    let actualUserId = userId;
    if (!isMongoId) {
      // Check cache first
      if (userIdCache[userId]) {
        actualUserId = userIdCache[userId];
      } else {
        // Otherwise perform lookup
        const user = await getUserByUsername(userId);
        if (user && (user.id || user._id)) {
          actualUserId = user.id || user._id;
          // Cache for future lookups
          userIdCache[userId] = actualUserId;
        } else {
          throw new Error(`Could not find user with username: ${userId}`);
        }
      }
    }
    
    // Now we should have a proper MongoDB ID to use with the profile endpoint
    const response = await axios.get(makeApiPath(`/users/${actualUserId}/profile`), { headers });
    
    // Ensure id property is set
    if (response.data._id && !response.data.id) {
      response.data.id = response.data._id;
    }
    
    // If the backend doesn't support metrics yet, create default metrics
    if (!response.data.hasOwnProperty('publicDebatesCount')) {
      // Fallback to basic user info with default metrics
      try {
        const userResponse = await getUserById(actualUserId);
        const userDebatesResponse = await axios.get(makeApiPath(`/debates/user/${actualUserId}`), { headers });
        
        const debates = userDebatesResponse.data || [];
        const publicDebates = debates.filter((d: any) => d.isPublic);
        const wonDebates = debates.filter((d: any) => d.winner === actualUserId);
        
        // Create profile with calculated metrics
        return {
          ...userResponse,
          publicDebatesCount: publicDebates.length,
          debatesWonCount: wonDebates.length,
          totalDebatesCount: debates.length,
          winRate: debates.length > 0 ? (wonDebates.length / debates.length) * 100 : 0,
          excaliburIndex: calculateExcaliburIndex(debates, wonDebates.length, publicDebates.length),
          debates: debates.map((d: any) => ({
            id: d._id || d.id,
            topic: d.topic,
            createdAt: d.createdAt,
            status: d.status,
            isWinner: d.winner === actualUserId
          }))
        };
      } catch (fallbackError) {
        logger.error('Error creating fallback user profile:', fallbackError);
        // Return a basic profile with default values
        return {
          ...response.data,
          publicDebatesCount: 0,
          debatesWonCount: 0,
          totalDebatesCount: 0,
          winRate: 0,
          excaliburIndex: 0,
          debates: []
        };
      }
    }
    
    return response.data;
  } catch (error) {
    logger.error(`Error fetching profile for user ${userId}:`, error);
    throw error;
  }
};

// Helper function to calculate Excalibur Index - a measure of user skill
const calculateExcaliburIndex = (debates: any[], wonCount: number, publicCount: number): number => {
  // Example formula - can be adjusted:
  // Base: 10 points per win 
  // Bonus: 5 points per public debate
  // Scaling: dividing by 10 to keep the number manageable
  const baseScore = wonCount * 10;
  const publicBonus = publicCount * 5;
  
  return Math.round((baseScore + publicBonus) / 10);
};

// Get multiple users by IDs - with fallback to avoid errors
export const getUsersByIds = async (userIds: string[]): Promise<{[key: string]: User}> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      logger.warn('No auth token found, cannot fetch user data');
      return {};
    }
    
    try {
      // Try to use the batch endpoint
      const response = await axios.post(makeApiPath('/users/batch'), {
        userIds
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Convert array to object with user ID as key
      const usersMap: {[key: string]: User} = {};
      
      if (Array.isArray(response.data)) {
        response.data.forEach(user => {
          const id = user._id || user.id;
          if (id) {
            if (!user.id && user._id) {
              user.id = user._id;
            }
            usersMap[id] = user;
          }
        });
      }
      
      return usersMap;
    } catch (batchError) {
      logger.warn('Batch user endpoint not available, falling back to individual requests:', batchError);
      // Fallback to individual requests if batch endpoint doesn't exist
      const usersMap: {[key: string]: User} = {};
      
      // Get current user as a fallback
      try {
        const currentUserResponse = await axios.get(makeApiPath('/auth/user'), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (currentUserResponse.data && currentUserResponse.data._id) {
          const user = currentUserResponse.data;
          // Ensure id property is set
          if (!user.id && user._id) {
            user.id = user._id;
          }
          usersMap[user.id] = user;
        }
      } catch (authError) {
        logger.error('Error fetching current user:', authError);
      }
      
      return usersMap;
    }
  } catch (error) {
    logger.error('Error in getUsersByIds:', error);
    return {};
  }
};

// CUBE contributions interface
export interface CubeContributions {
  activeCubeContributions: number;
  totalCubeContributions: number;
  contributionsByStatus: {
    active: number;
    challenged: number;
    updated: number;
  };
  cubeExplanations: Array<{
    topicId: string;
    explanationIndex: number;
    contributionType: 'original' | 'team_member';
    status: 'active' | 'challenged' | 'updated';
    addedAt: string;
  }>;
}

// Get user's CUBE contributions
export const getUserCubeContributions = async (): Promise<CubeContributions> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No auth token found');
    }
    
    const response = await axios.get(makeApiPath('/users/cube-contributions'), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    logger.error('Error fetching user CUBE contributions:', error);
    throw error;
  }
}; 